import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight, Ticket, Users } from 'lucide-react';
import type { EventItem } from '../../api/types';

const UpcomingEvents: React.FC<{ events?: EventItem[] }> = ({ events = [] }) => {
  if (!events || events.length === 0) return null;

  return (
    <section className="py-24 bg-[#FDF8F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="font-sans text-sm text-[#9CAF88] font-medium tracking-wider uppercase mb-2 block">Mark Your Calendar</span>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-[#3D3D3D] mb-4">Upcoming Events</h2>
          <p className="font-sans text-[#3D3D3D]/70 max-w-2xl mx-auto">Join us at local markets or dive deeper with our hands-on workshops</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.slice(0,3).map((event, index) => (
            <motion.div key={String(event.squareProductId ?? event.title ?? index)} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 group">
                <div className="relative">
                  <img src={event.imageUrls?.[0]} alt={event.title} className="w-full h-48 object-cover" />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full font-sans text-xs font-medium ${event.eventType === 'workshop' ? 'bg-[#C9A962] text-white' : 'bg-[#9CAF88] text-white'}`}>{event.eventType === 'workshop' ? 'Workshop' : 'Market Day'}</span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-serif text-xl font-semibold text-[#3D3D3D] mb-3 group-hover:text-[#D4A5A5] transition-colors">{event.title}</h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-[#3D3D3D]/70 font-sans text-sm">
                      <Calendar className="w-4 h-4 text-[#D4A5A5]" />
                      <span>
                        {event.startDate && new Date(event.startDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        {event.startTime && ` • ${event.startTime}`}
                      </span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-[#3D3D3D]/70 font-sans text-sm">
                        <MapPin className="w-4 h-4 text-[#D4A5A5]" />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>

                    {event.eventType === 'workshop' && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-[#C9A962]" />
                          <span className="font-sans font-medium text-[#3D3D3D]">{event.isDonation ? "Choose Your Price" : `${event.price}`}</span>
                        </div>
                        {event.capacity !== undefined && (
                          <div className="flex items-center gap-2 text-[#3D3D3D]/60">
                            <Users className="w-4 h-4" />
                            <span className="font-sans text-sm text-[#3D3D3D]/50">{event.capacity} spots available</span>
                          </div>
                        )}
                      </div>
                    )}

                  <Link to={`/events/${String(event.squareProductId ?? event.title ?? index)}`}>
                    <button className="w-full mt-4 bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white font-sans rounded-full px-5 py-3">
                      {event.eventType === 'workshop' ? 'Get Tickets' : 'Learn More'}
                    </button>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/events">
            <button className="border border-[#3D3D3D] text-[#3D3D3D] hover:bg-[#3D3D3D] hover:text-white font-sans rounded-full px-8 py-3 group">
              View All Events
              <ArrowRight className="ml-2 w-4 h-4 inline-block group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
